import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Divider
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Build as FixIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import {
  verifyDeliveryAgentMappings,
  fixDeliveryAgentMappings,
  getDeliveryAgentsWithUsers,
  DeliveryAgentWithUser
} from '../../utils/deliveryAgentUtils';

const DeliveryAgentVerification: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [verification, setVerification] = useState<{
    totalAgents: number;
    linkedAgents: number;
    unlinkedAgents: number;
    issues: string[];
  } | null>(null);
  const [agents, setAgents] = useState<DeliveryAgentWithUser[]>([]);
  const [fixResult, setFixResult] = useState<{
    fixed: number;
    errors: string[];
  } | null>(null);

  const runVerification = async () => {
    setLoading(true);
    setFixResult(null);
    try {
      const [verificationResult, agentsWithUsers] = await Promise.all([
        verifyDeliveryAgentMappings(),
        getDeliveryAgentsWithUsers()
      ]);
      
      setVerification(verificationResult);
      setAgents(agentsWithUsers);
    } catch (error) {
      console.error('Error running verification:', error);
    } finally {
      setLoading(false);
    }
  };

  const runFix = async () => {
    setFixing(true);
    try {
      const result = await fixDeliveryAgentMappings();
      setFixResult(result);
      
      // Re-run verification after fix
      await runVerification();
    } catch (error) {
      console.error('Error running fix:', error);
    } finally {
      setFixing(false);
    }
  };

  const getStatusColor = (isLinked: boolean) => {
    return isLinked ? 'success' : 'error';
  };

  const getStatusIcon = (isLinked: boolean) => {
    return isLinked ? <CheckIcon color="success" /> : <ErrorIcon color="error" />;
  };

  return (
    <Box>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Delivery Agent User Mapping Verification
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            This tool verifies that all delivery agents have properly linked user accounts for authentication and order assignment.
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={runVerification}
              disabled={loading || fixing}
            >
              {loading ? <CircularProgress size={20} /> : 'Run Verification'}
            </Button>
            
            {verification && verification.unlinkedAgents > 0 && (
              <Button
                variant="outlined"
                color="warning"
                startIcon={<FixIcon />}
                onClick={runFix}
                disabled={loading || fixing}
              >
                {fixing ? <CircularProgress size={20} /> : 'Fix Issues'}
              </Button>
            )}
          </Box>

          {verification && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Verification Results
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Chip 
                  label={`Total: ${verification.totalAgents}`} 
                  color="default" 
                />
                <Chip 
                  label={`Linked: ${verification.linkedAgents}`} 
                  color="success" 
                />
                <Chip 
                  label={`Unlinked: ${verification.unlinkedAgents}`} 
                  color={verification.unlinkedAgents > 0 ? 'error' : 'success'} 
                />
              </Box>

              {verification.unlinkedAgents === 0 ? (
                <Alert severity="success">
                  ✅ All delivery agents have properly linked user accounts!
                </Alert>
              ) : (
                <Alert severity="warning">
                  ⚠️ {verification.unlinkedAgents} delivery agent(s) have mapping issues that may affect delivery assignment.
                </Alert>
              )}

              {verification.issues.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Issues Found:
                  </Typography>
                  <List dense>
                    {verification.issues.map((issue, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <WarningIcon color="warning" />
                        </ListItemIcon>
                        <ListItemText primary={issue} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </Box>
          )}

          {fixResult && (
            <Box sx={{ mb: 3 }}>
              <Alert severity={fixResult.fixed > 0 ? 'success' : 'info'}>
                Fix completed: {fixResult.fixed} mapping(s) fixed
                {fixResult.errors.length > 0 && `, ${fixResult.errors.length} error(s)`}
              </Alert>
              
              {fixResult.errors.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Fix Errors:
                  </Typography>
                  <List dense>
                    {fixResult.errors.map((error, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <ErrorIcon color="error" />
                        </ListItemIcon>
                        <ListItemText primary={error} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </Box>
          )}

          {agents.length > 0 && (
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Delivery Agent Status
              </Typography>
              
              <List>
                {agents.map((agent, index) => (
                  <React.Fragment key={agent.deliveryPerson.id}>
                    <ListItem>
                      <ListItemIcon>
                        {getStatusIcon(agent.isLinked)}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body1">
                              {agent.deliveryPerson.name}
                            </Typography>
                            <Chip
                              size="small"
                              label={agent.isLinked ? 'Linked' : 'Not Linked'}
                              color={getStatusColor(agent.isLinked)}
                            />
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Email: {agent.deliveryPerson.email}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Delivery Person ID: {agent.deliveryPerson.id}
                            </Typography>
                            {agent.user && (
                              <Typography variant="body2" color="text.secondary">
                                Auth User ID: {agent.user.id}
                              </Typography>
                            )}
                            {!agent.isLinked && (
                              <Typography variant="body2" color="error">
                                ⚠️ This agent cannot receive order assignments
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < agents.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default DeliveryAgentVerification;
